import {
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';

import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import {
  ReactConsentContext,
  useSelectedWorkspace,
} from '../../Flow/ConsentContextProvider';
import {
  type McpContext,
  ReactMcpContext,
  MCP_POLICY_STATUS,
} from './McpContextProvider';
import { mcpPermissionsMessages } from './McpPermissions/messages';
import LegalDisclaimer from './LegalDisclaimer/LegalDisclaimer';

const mcpBottomBarStyles = xcss({
  flexShrink: 0,
  borderTopWidth: 'border.width',
  borderTopStyle: 'solid',
  borderTopColor: 'color.border',
  width: '100%',
  boxSizing: 'border-box',
  position: 'fixed',
  bottom: 'space.0',
  left: 'space.0',
  padding: 'space.300',
  backgroundColor: 'elevation.surface',
});

const mcpBottomBarInnerStyle = xcss({
  maxWidth: '1280px',
  margin: '0 auto',
  width: '100%',
});

const mcpFooterLayoutStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 'space.150',
  width: '100%',
  [media.above.sm]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 'space.400',
  },
});

const mcpInfoColumnStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 'space.050',
  minWidth: '0',
  textAlign: 'right',
});

const messages = defineMessages({
  accept: {
    id: 'footer.accept',
    defaultMessage: 'Accept',
    description:
      'Button to consent for this app to have the listed permissions',
  },
  cancel: {
    id: 'footer.cancel',
    defaultMessage: 'Cancel',
    description:
      'Button to cancel the consent for this app to have the listed permissions',
  },
});

const actionsOnlyStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 'space.200',
  justifyContent: 'flex-end',
  flexShrink: 0,
});

function mcpAcceptDisabled(
  mcpContext: McpContext,
  selectedWorkspace: string | null,
): boolean {
  // In the Trello-only flow, no Atlassian site is selected.
  // Accept is allowed if a Trello workspace has been selected instead.
  if (!selectedWorkspace && mcpContext.selectedTrelloWorkspaces.length === 0) {
    return true;
  }
  if (mcpContext.mcpPolicyStatus !== MCP_POLICY_STATUS.success) {
    return true;
  }
  if (mcpContext.mcpRedirectAllowed === false) {
    return true;
  }
  if (mcpContext.mcpByIntent.length === 0) {
    return true;
  }
  if (mcpContext.mcpSelectedToolsetNames.size === 0) {
    return true;
  }
  return false;
}

type ButtonDecision = 'accept' | 'cancel';

const MCPFooter: FunctionComponent = () => {
  const analytics = useAnalytics();
  const consentContext = useContext(ReactConsentContext);
  const mcpContext = useContext(ReactMcpContext);
  const { selectedWorkspace } = useSelectedWorkspace();
  const [clickedButton, setClickedButton] = useState<ButtonDecision | null>(
    null,
  );

  const fireAnalyticEvent = useCallback(
    (buttonName: string, buttonValue: ButtonDecision) => () => {
      analytics.sendButtonClickEvent(buttonName);
      setTimeout(() => setClickedButton(buttonValue), 0);
    },
    [analytics],
  );

  const acceptDisabled = useMemo(() => {
    if (!consentContext?.token) {
      return false;
    }
    if (consentContext.token.serviceType === 'MCP') {
      if (!mcpContext) {
        return true;
      }
      return mcpAcceptDisabled(mcpContext, selectedWorkspace);
    }
    // For non-MCP service types, no additional disable conditions
    return false;
  }, [consentContext, mcpContext, selectedWorkspace]);

  const buttons = (
    <ButtonGroup>
      <Button
        type="submit"
        appearance="subtle"
        name="decision"
        value="cancel"
        onClick={fireAnalyticEvent('cancelConsent', 'cancel')}
        isDisabled={clickedButton !== null}
      >
        <FormattedMessage {...messages.cancel} />
      </Button>
      <Button
        type="submit"
        appearance="primary"
        name="decision"
        value="accept"
        isDisabled={acceptDisabled || clickedButton !== null}
        onClick={fireAnalyticEvent('acceptConsent', 'accept')}
      >
        <FormattedMessage {...messages.accept} />
      </Button>
    </ButtonGroup>
  );

  const showToolsetsCount =
    consentContext?.token.serviceType === 'MCP' &&
    mcpContext?.isEditingPermissions;

  return (
    <Box xcss={mcpBottomBarStyles}>
      <Box xcss={mcpBottomBarInnerStyle}>
        <Box xcss={mcpFooterLayoutStyles}>
          <Box xcss={mcpInfoColumnStyles}>
            {showToolsetsCount && mcpContext && (
              <Text as="span" color="color.text.subtle" weight="bold">
                <FormattedMessage
                  {...mcpPermissionsMessages.toolsetsSelected}
                  values={{
                    selected: mcpContext.mcpSelectedToolsetNames.size,
                    total: mcpContext.mcpByIntent.reduce(
                      (n, g) => n + g.toolsets.length,
                      0,
                    ),
                  }}
                />
              </Text>
            )}
            <LegalDisclaimer />
          </Box>
          <Box xcss={actionsOnlyStyles}>{buttons}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MCPFooter;
